import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { celebrate } from "@/lib/daily";

export default function EventDialog({ open, onClose, rerollCost, refresh }) {
  const [ev, setEv] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (open) api.get("/game/event").then(({ data }) => setEv(data)).catch(() => onClose());
  }, [open, onClose]);

  const act = async (fn) => {
    setBusy(true);
    try { const { data } = await fn(); setEv((e) => ({ ...e, pending: false, ...data })); celebrate(data.quests_done); await refresh(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !ev?.pending && onClose()}>
      <DialogContent data-testid="event-dialog" className={`glass max-w-lg ${!ev || ev.pending ? "[&>button]:hidden" : ""}`} aria-describedby={undefined}>
        {!ev && <DialogTitle className="sr-only">Mystery event</DialogTitle>}
        {ev && (
          <>
            <DialogHeader>
              <p className="text-[11px] font-mono-game text-purple-300">TWO PATHS</p>
              <DialogTitle className="font-display text-2xl text-amber-100">{ev.event.title}</DialogTitle>
              <DialogDescription className="text-slate-300 pt-2">{ev.event.text}</DialogDescription>
            </DialogHeader>
            {ev.pending ? (
              <div className="grid gap-3 pt-2">
                {ev.event.choices.map((c) => (
                  <button key={c.id} data-testid={`event-choice-${c.id}`} disabled={busy} onClick={() => act(() => api.post("/game/event/choose", { choice: c.id }))}
                    className="text-left px-4 py-3 rounded-lg border border-slate-700 hover:border-fuchsia-500 hover:bg-fuchsia-500/10 transition-colors">{c.label}</button>
                ))}
              </div>
            ) : (
              <div className="space-y-4 pt-2">
                <p data-testid="event-result" className={`p-4 rounded-lg border ${ev.result?.good ? "border-emerald-600/50 bg-emerald-950/40 text-emerald-200" : "border-red-600/50 bg-red-950/40 text-red-200"}`}>{ev.result?.text}{ev.result?.credits > 0 && <span data-testid="event-credits" className="block mt-1 font-mono-game text-amber-200">+{ev.result.credits} credits</span>}</p>
                <div className="flex justify-between gap-3">
                  {ev.can_reroll && (
                    <button data-testid="event-reroll-btn" disabled={busy} onClick={() => act(() => api.post("/game/event/reroll"))} className="px-4 py-2 rounded-full text-sm border border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
                      Re-roll fate · {rerollCost} credits
                    </button>
                  )}
                  {ev.result?.battle_id
                    ? <button data-testid="event-fight-btn" onClick={() => { onClose(); nav(`/battle/${ev.result.battle_id}`); }} className="ember-btn ml-auto px-6 py-2 rounded-full text-sm">FIGHT!</button>
                    : <button data-testid="event-continue-btn" onClick={onClose} className="ember-btn ml-auto px-6 py-2 rounded-full text-sm">CONTINUE</button>}
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
